import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CreditCard, Lock, Loader2 } from "lucide-react";
import { logError } from "@/lib/log";

const IFIELDS_KEY = process.env.REACT_APP_CARDKNOX_IFIELDS_KEY;
const IFIELDS_VERSION = "3.1.2507.1501";
const IFIELDS_SCRIPT_ID = "cardknox-ifields-script";

// Load Cardknox iFields JS lazily, exactly once.
let ifieldsScriptPromise = null;
function loadIFields() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.setAccount && window.getTokens) return Promise.resolve();
  if (ifieldsScriptPromise) return ifieldsScriptPromise;
  ifieldsScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(IFIELDS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = IFIELDS_SCRIPT_ID;
    s.src = `https://cdn.cardknox.com/ifields/${IFIELDS_VERSION}/ifields.min.js`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
  return ifieldsScriptPromise;
}

/**
 * Cardknox iFields payment dialog.
 *
 * Four modes:
 *  - `mode="pay"` (default) — charge `invoice.total` against the new card.
 *  - `mode="pay-order"` — charge a placed order at `invoice.id`.
 *  - `mode="pay-order-public"` — charge a placed order via its public
 *    `payToken` (no login required, used by the email-link page).
 *  - `mode="save-only"` — just save a card on file for `customerId`, no charge.
 */
export default function CardknoxPayDialog({ open, onClose, invoice, customerId, mode = "pay", payToken, onPaid, allowSaveCard = true }) {
  const [ready, setReady] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exp, setExp] = useState("");
  const [zip, setZip] = useState("");
  const [saveCard, setSaveCard] = useState(true);

  const cardTokenInputRef = useRef(null);
  const cvvTokenInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!IFIELDS_KEY) {
      setLoadErr("Card processor is not configured (missing iFields key).");
      return;
    }
    setLoadErr(null);
    loadIFields().then(() => {
      if (typeof window.setAccount !== "function") {
        setLoadErr("Card processor failed to initialize.");
        return;
      }
      try {
        window.setAccount(IFIELDS_KEY, "CatskillsPoolSupplies", "1.0");
        if (typeof window.enableAutoFormatting === "function") {
          try { window.enableAutoFormatting(" "); } catch (fmtErr) {
            logError("Cardknox enableAutoFormatting unsupported in this iFields version:", fmtErr);
          }
        }
        setReady(true);
      } catch (e) {
        setLoadErr(formatErr(e?.message) || "Card processor init error.");
      }
    }).catch(() => setLoadErr("Could not load card processor. Check your internet connection."));
  }, [open]);

  // Reset state when dialog closes.
  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setExp("");
      setZip("");
      setSaveCard(true);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!ready || submitting) return;
    if (!exp || exp.length !== 4) {
      toast.error("Enter expiry as MMYY (e.g. 1230).");
      return;
    }
    setSubmitting(true);
    try {
      // Ask Cardknox to swap the iframe contents for opaque tokens.
      await new Promise((resolve, reject) => {
        const onSuccess = () => resolve();
        const onError = (err) => reject(new Error(err?.error || err?.message || "Could not tokenize the card."));
        try {
          window.getTokens(onSuccess, onError, 15000);
        } catch (err) {
          reject(err);
        }
      });
      const cardToken = cardTokenInputRef.current?.value || "";
      const cvvToken = cvvTokenInputRef.current?.value || "";
      if (!cardToken) {
        throw new Error("Card was not tokenized — please re-enter the card number.");
      }

      if (mode === "save-only") {
        const { data } = await api.post("/payments/cardknox/save-card", {
          customer_id: customerId,
          xtoken: cardToken,
          xexp: exp,
          xcvv: cvvToken,
        });
        toast.success(`Card ending ${data.last4 || "????"} saved.`);
        onPaid?.(data);
        onClose?.();
        return;
      }

      if (mode === "pay-order") {
        const { data } = await api.post("/payments/cardknox/charge-order", {
          order_id: invoice.id,
          xtoken: cardToken,
          xexp: exp,
          xcvv: cvvToken,
          save_card: saveCard,
        });
        toast.success(`Order paid · Ref ${data?.ref_num || ""}`);
        onPaid?.(data);
        onClose?.();
        return;
      }

      if (mode === "pay-order-public") {
        // Public no-login charge using the email-link pay token.
        const baseURL = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${baseURL}/api/orders/by-pay-token/${payToken}/charge`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ xtoken: cardToken, xexp: exp, xcvv: cvvToken }),
        });
        if (!res.ok) {
          let detail = "Payment failed.";
          try { detail = (await res.json())?.detail || detail; } catch (parseErr) {
            logError("Cardknox public-pay error response was not JSON:", parseErr);
          }
          throw new Error(detail);
        }
        toast.success("Payment received — thank you!");
        onPaid?.();
        onClose?.();
        return;
      }

      const { data } = await api.post("/payments/cardknox/charge", {
        invoice_id: invoice.id,
        xtoken: cardToken,
        xexp: exp,
        xcvv: cvvToken,
        save_card: saveCard,
        zip: zip || undefined,
      });
      toast.success(`Payment approved · Ref ${data.ref_num || ""}`);
      onPaid?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatErr(err?.response?.data?.detail) || err?.message || "Payment failed.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose?.(); }}>
      <DialogContent className="max-w-md rounded-sm" data-testid="cardknox-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {mode === "save-only" ? "Add payment method"
              : (mode === "pay-order" || mode === "pay-order-public") ? `Pay order ${invoice?.number}`
              : `Pay invoice ${invoice?.number}`}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1 text-xs">
            <Lock className="w-3 h-3" /> Card data is sent directly to Cardknox — it never touches our servers.
          </DialogDescription>
        </DialogHeader>

        {loadErr ? (
          <p className="text-sm text-destructive" data-testid="cardknox-load-error">{loadErr}</p>
        ) : !ready ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading secure card form…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Hidden inputs Cardknox writes tokens into when getTokens() succeeds. */}
            <input type="hidden" name="xCardNum" data-ifields-id="card-number-token" ref={cardTokenInputRef} />
            <input type="hidden" name="xCVV" data-ifields-id="cvv-token" ref={cvvTokenInputRef} />

            <div>
              <Label htmlFor="ck-card-number">Card number</Label>
              <iframe
                id="ck-card-number"
                data-ifields-id="card-number"
                data-ifields-placeholder="Card number"
                src={`https://cdn.cardknox.com/ifields/${IFIELDS_VERSION}/ifield.htm`}
                title="Card number"
                className="w-full h-10 border border-input rounded-sm bg-background"
                style={{ display: "block" }}
                data-testid="cardknox-card-iframe"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ck-exp">Expiry (MMYY)</Label>
                <Input
                  id="ck-exp"
                  inputMode="numeric"
                  value={exp}
                  maxLength={4}
                  onChange={(ev) => setExp(ev.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="1230"
                  className="rounded-sm mono-num"
                  data-testid="cardknox-exp-input"
                />
              </div>
              <div>
                <Label htmlFor="ck-cvv">CVV</Label>
                <iframe
                  id="ck-cvv"
                  data-ifields-id="cvv"
                  data-ifields-placeholder="CVV"
                  src={`https://cdn.cardknox.com/ifields/${IFIELDS_VERSION}/ifield.htm`}
                  title="CVV"
                  className="w-full h-10 border border-input rounded-sm bg-background"
                  style={{ display: "block" }}
                  data-testid="cardknox-cvv-iframe"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ck-zip">Billing ZIP <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                id="ck-zip"
                inputMode="numeric"
                value={zip}
                maxLength={10}
                onChange={(ev) => setZip(ev.target.value.replace(/[^\d-]/g, "").slice(0, 10))}
                placeholder="12754"
                className="rounded-sm mono-num"
                data-testid="cardknox-zip-input"
              />
            </div>
            {allowSaveCard && mode !== "save-only" && (
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <Checkbox checked={saveCard} onCheckedChange={(v) => setSaveCard(!!v)} data-testid="cardknox-save-card" />
                <span className="text-sm">Save this card for future deliveries</span>
              </label>
            )}
            <div className="flex items-center justify-between pt-2">
              {mode === "save-only" ? (
                <p className="text-base font-semibold">No charge today</p>
              ) : (
                <p className="text-base font-semibold">Total · ${Number(invoice?.total || 0).toFixed(2)}</p>
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-sm" onClick={onClose} disabled={submitting} data-testid="cardknox-cancel">
                  Cancel
                </Button>
                <Button type="submit" className="rounded-sm" disabled={submitting} data-testid="cardknox-pay-submit">
                  {submitting
                    ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {mode === "save-only" ? "Saving…" : "Charging…"}</>)
                    : (mode === "save-only" ? "Save card" : `Pay $${Number(invoice?.total || 0).toFixed(2)}`)}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
